import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { dayjs, getDiffInDays } from '@hozana/dates'
import { HtmlParser } from '@hozana/dom/components/HtmlParser'
import { Translate } from '@hozana/intl/Translate'

import { Div } from 'elements/layout/Div'
import { P } from 'elements/text/P'

import { type TCommunity } from 'modules/community/types'

import { isNovenaOrRetreat } from '../functions'
import { useCommunityFrequency } from '../hooks/useCommunityFrequency'

type TWordingVariantProps = {
  children: ReactNode[]
  className: string
}

const WordingVariant: TFCC<TWordingVariantProps> = ({ children, className }) => (
  <Div className={className} align="left" w="100%">
    {React.Children.map(children, (child) => (
      <P fontSize="1.1em" m="0 0 10px">
        <strong>•</strong>&nbsp;
        {child}
      </P>
    ))}
  </Div>
)

export type TCommunityLandingWordings = {
  community: TCommunity
}

export const CommunityLandingWordings: TFC<TCommunityLandingWordings> = ({ community }) => {
  const { frequencyType, frequencyText } = useCommunityFrequency({ community })
  const { t } = useTranslation()
  const novenaOrRetreat = isNovenaOrRetreat(community)
  const retreatStatus = community.retreatStatus
  const { fromDate, toDate, lifetime } = community
  const nbDays = getDiffInDays(fromDate, toDate) + 1
  const formattedFromDate = dayjs(fromDate).format('DD MMMM')
  const formattedToDate = dayjs(toDate).format('DD MMMM YYYY')

  return (
    <WordingVariant className="cp-landing-page-version-b">
      <Translate
        values={{
          fromDate: formattedFromDate,
          toDate: formattedToDate,
          nbDays,
          frequencyText: frequencyText.toLowerCase(),
          lifetime,
          frequencyType,
        }}
      >
        {novenaOrRetreat && retreatStatus.toRediscover
          ? 'trans:community:community.join.subscribe.line1.to-rediscover'
          : 'trans:community:community.join.subscribe.line1.default'}
      </Translate>
      <Translate
        values={{
          lifetime: community.lifetime,
          nbMembers: community.nbMembers,
          status: dayjs(community.fromDate).isAfter(dayjs())
            ? 'notStarted'
            : dayjs(community.toDate).isBefore(dayjs())
            ? 'finished'
            : 'inProgress',
        }}
      >
        trans:community:community.join.subscribe.line2
      </Translate>
      <HtmlParser>{t('community:community.join.subscribe.line3')}</HtmlParser>
    </WordingVariant>
  )
}
